import {QueryClient} from '@components/@query';
import {InfiniteData} from '@tanstack/react-query';
import {cloneDeep} from 'lodash';

export const updateInfiniteQueryData = <T, K>(
    queryKey: (string | number)[],
    selector: ((data: T) => K[]) | null,
    matcher: (item: K) => boolean,
    replacer: (prevItem: K | null) => K | null | undefined,
    targetPageIdx = 0,
    targetItemIdx = 0,
) => {
    const select = selector || ((data: T) => data as K[]);

    QueryClient.setQueryData(
        queryKey,
        (prevData?: InfiniteData<T>) => {
            if (!prevData?.pages.length) {
                QueryClient.invalidateQueries({queryKey});
                return prevData;
            }

            const newPages = cloneDeep(prevData.pages);

            let pageIdx = -1;
            let itemIdx = -1;

            for (let pIdx = 0; pIdx < prevData.pages.length; pIdx++) {
                for (
                    let iIdx = 0;
                    iIdx < select(prevData.pages[pIdx]).length;
                    iIdx++
                ) {
                    if (matcher(select(prevData.pages[pIdx])[iIdx])) {
                        pageIdx = pIdx;
                        itemIdx = iIdx;
                        break;
                    }
                }
                if (pageIdx !== -1) {
                    break;
                }
            }

            if (itemIdx === -1) {
                // if item doesn't exist in query and provided selector is null
                // (meaning pages are arrays of these items), we can attempt to insert
                // it directly.
                const newItem = replacer(null);

                if (newItem && !selector) {
                    select(newPages[targetPageIdx]).splice(
                        targetItemIdx,
                        0,
                        newItem,
                    );
                } else {
                    QueryClient.invalidateQueries({queryKey});
                    return prevData;
                }
            } else {
                const prevItem = select(newPages[pageIdx])[itemIdx];
                const newItem = replacer(prevItem);

                if (newItem) {
                    select(newPages[pageIdx]).splice(itemIdx, 1, newItem);
                } else {
                    select(newPages[pageIdx]).splice(itemIdx, 1);
                }
            }

            return {pages: newPages, pageParams: prevData.pageParams};
        },
        {
            updatedAt: new Date().getTime(),
        },
    );
};
